import { NgModule } from '@angular/core';
import { ApolloModule, APOLLO_OPTIONS } from 'apollo-angular';
import { ApolloClientOptions, InMemoryCache, ApolloLink } from '@apollo/client/core';
import { HttpLink } from 'apollo-angular/http';
import { setContext } from '@apollo/client/link/context';
import { CookieService } from 'ngx-cookie-service';
import { StorageService } from '../utils/services/storage.service';
import { environment } from '../../environments/environment';

const TOKEN_KEY = environment.tokenSessionName;

function graphqlUri(): string {
    return environment.envVar.API_URL + '/graphql';
}

// La cookie/sessionStorage 'current_company' guarda el SLUG cifrado de la company (lo pone
// app vía postMessage, ver app.component.ts), no el id numérico crudo que exige el middleware
// InjectTenant de api-2 (ctype_digit). El id numérico real vive en storageService.get('company').id
// (lo puebla CompanyGuard con la respuesta real de /companies/{slug}). Se valida con regex antes
// de usar cualquier fallback para no enviar nunca el slug donde se espera un entero.
function resolveCompanyId(storageService: StorageService, cookieService: CookieService): string {
    const company = storageService.get('company');
    if (company && company.id && /^\d+$/.test(String(company.id))) {
        return String(company.id);
    }

    const fallback = cookieService.get('current_company') || sessionStorage.getItem('current_company') || '';
    return /^\d+$/.test(fallback) ? fallback : '';
}

export function createApollo(
    httpLink: HttpLink,
    cookieService: CookieService,
    storageService: StorageService,
): ApolloClientOptions<any> {
    const authLink = setContext(() => {
        const token = sessionStorage.getItem(TOKEN_KEY);
        const companyId = resolveCompanyId(storageService, cookieService);

        const headers: Record<string, string> = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
        if (companyId) {
            headers['X-Company-Id'] = companyId;
        }

        return { headers };
    });

    return {
        link: ApolloLink.from([authLink, httpLink.create({ uri: graphqlUri() })]),
        cache: new InMemoryCache(),
        defaultOptions: {
            query: { fetchPolicy: 'network-only' },
        },
    };
}

@NgModule({
    imports: [ApolloModule],
    providers: [
        {
            provide: APOLLO_OPTIONS,
            useFactory: createApollo,
            deps: [HttpLink, CookieService, StorageService],
        },
    ],
})
export class GraphQLModule {}
